import type { CommonContextStore } from 'src/types/context'
import type { RateFile } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'

const STAGE_MEDIA_BASE = 'https://stage.prodoctorov.com/media/'
const NO_AVATAR_LPU = '/static/_v1/pd/icons/ui-kit/photo/no-avatar-lpu.svg'
const NO_AVATAR_DOCTOR = '/static/_v1/pd/icons/ui-kit/photo/no-avatar.svg'
const NO_AVATAR_DOCTOR_HIDDEN = '/static/_v1/pd/icons/ui-kit/photo/no-avatar-with-text.svg'

function getMediaBase(context: CommonContextStore['context']) {
  if (context.global?.env?.isProd) {
    const media = context.global.static?.media || ''

    return media.endsWith('/') ? media : `${media}/`
  }

  return STAGE_MEDIA_BASE
}

/**
 * Путь к файлу из поля Django File / Image.
 *
 * Пустой файл приходит объектом `{ __bool__: false, url: null }`,
 * а не `null`: без этой проверки «нет аватара» выглядит как битая картинка.
 */
export function resolveFilePath(value: RateFile | undefined): string {
  if (!value) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  if (value.__bool__ === false) { // eslint-disable-line no-underscore-dangle
    return ''
  }

  return value.url || value.name || value.__str__ || '' // eslint-disable-line no-underscore-dangle
}

/**
 * Абсолютный URL медиа для `<img>`.
 *
 * На stage `context.static.media` часто указывает на локальный путь.
 * В браузере его нет — берётся stage-CDN.
 * Уже абсолютные и `/static/` / `/media/` не переписываются.
 */
export function getMediaUrl(
  value: RateFile | undefined,
  context: CommonContextStore['context'],
): string {
  const path = resolveFilePath(value)

  if (!path) {
    return ''
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('/static/') || path.startsWith('/media/')) {
    return path
  }

  return `${getMediaBase(context)}${path.replace(/^\//, '')}`
}

/**
 * Аватар клиники в карточке: сначала логотип ответа, затем обрезанный логотип.
 *
 * Пустой `avatar_reply` должен провалиться дальше по цепочке,
 * а не остановить выбор на «ложном» File-объекте.
 */
export function getLpuAvatarUrl(
  lpu: {
    avatar_reply?: RateFile
    cropped_logo?: RateFile
    img_50?: RateFile
    img_100?: RateFile
    img_200?: RateFile
    logo?: {
      priority_image_files?: Array<{
        file: {
          url: string
        }
        file_format: string
      }>
    }
  } | null | undefined,
  context: CommonContextStore['context'],
): string {
  if (!lpu) {
    return NO_AVATAR_LPU
  }

  const avatarReplyUrl = getMediaUrl(lpu.avatar_reply, context)

  if (avatarReplyUrl) {
    return avatarReplyUrl
  }

  if (context.request?.feature_flags?.ff_update_lpu_logo) {
    const files = lpu.logo?.priority_image_files || []
    const lastImageFile = files[files.length - 1]

    return lastImageFile?.file?.url || NO_AVATAR_LPU
  }

  const url = [
    lpu.cropped_logo,
    lpu.img_50,
    lpu.img_100,
    lpu.img_200,
  ]
    .map(candidate => getMediaUrl(candidate, context))
    .find(Boolean)

  return url || NO_AVATAR_LPU
}

/**
 * Аватар врача в карточке отзыва.
 *
 * При `hide_avatar` показывается заглушка с текстом, а не пустой слот.
 * `dt_modified` в query — чтобы после смены фото не оставался кэш CDN.
 */
export function getDoctorAvatarUrl(
  doctor: {
    hide_avatar?: boolean
    square_avatar_small?: RateFile
    square_avatar?: RateFile
    dt_modified?: string
  } | null | undefined,
  context: CommonContextStore['context'],
): string {
  if (!doctor) {
    return NO_AVATAR_DOCTOR
  }

  if (doctor.hide_avatar) {
    return NO_AVATAR_DOCTOR_HIDDEN
  }

  const url = getMediaUrl(doctor.square_avatar_small || doctor.square_avatar, context)

  if (!url) {
    return NO_AVATAR_DOCTOR
  }

  if (doctor.dt_modified) {
    const separator = url.includes('?') ? '&' : '?'

    return `${url}${separator}t=${encodeURIComponent(doctor.dt_modified)}`
  }

  return url
}
